import { createApp, reactive, computed, ref, readonly } from "vue";
import Cell from "../vue/Cell.vue";
import AutoModal from "../vue/AutoModal.vue";
import AutoAIModal from "../view/components/autoAI/AutoAIModal.vue";
import { MODAL_ID } from "./enums/modal_types";
import { readSlots, goDateString, dateFromSecondsTimestamp, createStartAndEndDatesFromArray, roundTwoDecimals, roundNum, timestampSecsFromDate } from './common';
import { getUrlParam, fromOplusTime, toOplusTime } from "../common/common";
import { mountTagRoleFilterModal } from "./modal";
import { transformCompany, transformUser } from "./transformers";
import { slotPostRequest, slotPutRequest, slotDeleteRequest, getPageSlotURL } from "./slot_crud_requests"
import { DELETE_SLOT_PARAM_STR, SLOT_SUBMIT_BUTTON_ID, handleCreateSuccess, handleUpdateSuccess, handleDeleteSuccess, handleTemplateCreateSuccess, DELETE_REPEAT_INPUT_VALUE } from "./slot_crud_common"
import { ACTIONS_TYPE, undoActionRequest, addToUndoStack, getLastUndoStack, removeDeletedSlotInStack, disableUndoLoading, removeLastActionFromUndoStack, enableUndoLoading, disableSaveUndo } from "./slot_undo";
import { RoleStore } from "../store/v1/roleStore";
import { ShiftStore } from "../store/v1/shiftStore";
import periodTypes from "../table/v1/periodTypes";
import * as shiftParser from '../legacy/parser/shiftParser';
import { EmploymentShiftTableStats } from "../employmentShiftTable/employmentShiftTableStats";
import { MeStore } from "../store/v1/meStore";
import { mergeLinkedShifts, initPlanFunctions, makeComputedValuesMap, newBranchStore, newBranchFromRaw, newMeStore, newRoleStoreFromRaw, newEmploymentStoreFromRaw, calcTableStartDate, calcViewType, calcTableEndDate, getShiftAndOptionsForStats, newSummaryStats, addRawSlotToShiftStore, getDateStringsForSlot, addSlotToCellsMap, buildCellsMap, computeRequestedVacationByUser, computeMonthlyLaborFlags } from "../employmentShiftTable/employmentShiftTable";

import { BranchStore } from "../store/v1/branchStore";
import * as branchParser from '../legacy/parser/branchParser';
import * as branchSettingsParser from '../legacy/parser/branchSettingsParser';
import * as roleParser from '../legacy/parser/roleParser';
import * as employmentParser from '../legacy/parser/employmentParser';
import { store as roleStoreOld } from "./stores/role_store"
import * as ccbiz from "./ccbiz";
import { EmploymentStore } from "../store/v1/employmentStore";
import { calcEndDate, calcStartDate, endDateForPeriod, makeTableDates, truncate } from "../table/v1/table";
import viewTypes from "../table/v1/viewTypes";
import { DateTime } from "luxon";

import { User } from "../model/v1/user";
import { PlanFunctionStore } from "../store/v1/planFunction";
import { PlanValueStore } from "../store/v2/planValue";
import { evaluatePlanFunction } from "../plan/v1/plan";
import { PlanVariableStore } from "../store/v1/planVariable";
import { initLegacyFunctions } from "../legacy/plan/legacyPlan";

import * as kot from "./kot";
import * as smarthr from "./smarthr";
import { getAndUpdateRolesList, initSlotModalRoleListComponent } from "../slotModal/roleList";
import { cellEditMode, initCellEdit, openCellEditMenuOnBtn, resetSelectedCell } from "../summaryTable/tableEditMode";
import { reactiveSlotLogMap, initSlotLogMap } from "./slotLog";
import CellEditMenuWrapper from "../summaryTable/components/CellEditMenuWrapper.vue";
import { ColumnWidthOptions } from "../common/tableConfig";
import { renderAllStatsColumns, renderAllStatsRows } from "./summary_common";
import { autoAIPresenterInjectKey, autoAIViewModelInjectKey } from "../provider/provider";
import { AutoAIPresenter } from "../presenter/autoAI";
import { initFooterAutoButtonClick } from "../view/components/autoAI/autoai_poll";
import { initFooterDropdownHandlers } from "./summary_footer";
import { SummaryTableApp } from "../app/summaryTable";

export { handleFooterBtnClick } from "./summary_footer";

let activeCom = null;
let activeUser;
let isUsingRequestSlots = false;

// Global stores and refs to support render / accept shift data from template
// Should be in a table component in the future
let tableDates;
let roleStore;
let meStore;
let shiftStore;
let employmentStore;
let branchStore;
let summaryStats;

export function getMeStore() {
  return meStore;
}

export function getEmploymentStore() {
  return employmentStore
}

export const deleteMode = reactive({
  isOn: false,
  selectedSlots: []
});

export function handleInitSlotLogMap(newSlotLogMap) {
  initSlotLogMap(newSlotLogMap);
}

function mountAutoModal(props, roles) {
  const app = createApp(AutoModal, {
    roles: roles,
    timePeriodType: props.timePeriodType,
    tags: props.tags,
    activeDate: props.activeDate,
    meStore,
  });
  window[MODAL_ID.AUTO_MODAL] = app.mount(MODAL_ID.AUTO_MODAL)
  $(`${MODAL_ID.AUTO_MODAL}-btn`).on("click", () => {
    window[MODAL_ID.AUTO_MODAL].openModal();
  })
}

function mountAutoAIModal(props, roles, start, end) {
  const app = createApp(AutoAIModal);
  const viewModel = ref({
    branchId: meStore.getActiveBranchId(),
    userId: meStore.getActiveUserId(),
    roles: roles,
    checkedRoles: [],
    tags: props.tags,
    until: props.urlParams.until,
    timePeriodType: props.timePeriodType,
    activeDate: props.activeDate,
    speed: 2,
    instructions: "",
    isVisible: false,
    isLoading: false,
    showRoles: true,
    isDone: false,
    notesJapanese: "",
    reloadOnDone: true,
    dateRange: { start, end },
  })

  const presenter = new AutoAIPresenter();
  presenter.setViewModel(viewModel.value)

  app.provide(autoAIViewModelInjectKey, readonly(viewModel));
  app.provide(autoAIPresenterInjectKey,  presenter);
  window["#auto-ai-modal"] = app.mount("#auto-ai-modal")
}

// Required for TS files to be able to support deprecated behavior while avoiding TS errors.
export function exposeInWindow(key, componentRef) {
  window[key] = componentRef;
}

export function getDateStringsForSlotPassThrough(slot, tz, repeatDate) {
  return getDateStringsForSlot(slot, tz, repeatDate);
}

// Required for summary.html to add a slot to cells map
export function addSlotToCellsMapPassThrough(cellsMap, s, splitNightShift, tz, repeatDate) {
  addSlotToCellsMap(cellsMap, s, splitNightShift, tz, repeatDate);
}

function computeEditModeCellProps(dates, users) {
  const out = {};
  dates.forEach((date, dateIdx) => {
    const ds = date.String;
    out[ds] = users.map((user, userIdx) => {
      return {
        userId: user.ID,
        prevUserId: users[userIdx - 1]?.ID || 0,
        nextUserId: users[userIdx + 1]?.ID || 0,
        prevDate: dates[dateIdx - 1]?.String || "",
        nextDate: dates[dateIdx + 1]?.String || "",
      };
    })
  })

  return out;
}

export function updateSelectedSlotsCount() {
  const text = document.getElementById("delete-slot-count");
  const count = deleteMode.selectedSlots.length;
  return text.innerText = count;
}

export function resetSelectedSlots() {
  deleteMode.selectedSlots = [];
  updateSelectedSlotsCount();
}

//findRoleById will use .find() on the dash base's App.roles data array with optional chaining. Returns a role object or undefined.
export function findRoleById(roleId) {
  return App?.roles?.find(r => r.ID == roleId);
}

//findUserById will use .find() on the dash base's App.userMap object with optional chaining. Returns an object with a subset of fields from a BranchUser or undefined.
export function findUserById(userId) {
  return App?.userMap?.[userId];
}

// Construct data object from Modal
function constructDataFromSlotModal(extract) {
  $("#slot-modal-from").removeClass("is-error");
  $("#slot-modal-to").removeClass("is-error");
  $("#slot-modal-purpose").removeClass("is-error");

  const fromInput = $("#slot-modal-from").val().trim();
  const toInput = $("#slot-modal-to").val().trim();
  const rawFrom = fromInput.length === 3 ? `0${fromInput}` : fromInput;
  const rawTo = toInput.length === 3 ? `0${toInput}` : toInput;
  const purpose = $("#slot-modal-purpose").val().trim();

  const parsedFrom = ExtractTimeAndMin(rawFrom, true);
  const parsedTo = ExtractTimeAndMin(rawTo, true);

  if (rawFrom !== "" && !parsedFrom.isValid) {
    $("#slot-modal-from").addClass("is-error");
    return;
  }
  if (rawTo !== "" && !parsedTo.isValid) {
    $("#slot-modal-to").addClass("is-error");
    return;
  }

  if (SlotModal.type == 0 && !SlotModal.roleID && !SlotModal.patternID) {
    if (rawFrom === "" && rawTo === "" && purpose === "") {
      $("#slot-modal-from").addClass("is-error");
      $("#slot-modal-to").addClass("is-error");
      $("#slot-modal-purpose").addClass("is-error");
      return null;
    }
  }

  const data = {
    "from": parsedFrom.toString(),
    "to": parsedTo.toString(),
    "comID": activeCom.ID,
    "date": SlotModal.slotDate,
    "type": SlotModal.type,
    "purpose": $("#slot-modal-purpose").val(),
    "locked": $("#slot-modal-lock-checkbox").prop("checked"),
    "rest": $("#slot-modal-rest").val(),
    "wage": $("#slot-modal-wage").val(),
    "sendEmail": $("#slot-modal-send-email-checkbox").prop("checked"),
    "help": $("#slot-modal-help").val(),
    "isMadeFromTemplate": true,
    "memo": $("#slot-modal-memo").val(),
  };

  if (!extract) {
    const repeat = $("#slot-modal-repeat-checkbox").prop("checked");
    if (repeat) {
      data["repeat"] = true;
    }
  } else {
    data["extractID"] = SlotModal.slotID;
  }

  // SlotModal.edited is a boolean flag to signify if a shift leader or owner is performing the slot creation or edit. This includes comments.
  if (SlotModal.edited) {
    data["edited"] = true;
  }

  if (SlotModal.userID) {
    data["userID"] = SlotModal.userID;
  }
  if (SlotModal.roleID) {
    data["roleID"] = SlotModal.roleID;
  }
  if (SlotModal.patternID) {
    data["patternID"] = SlotModal.patternID;
  }
  if (SlotModal.askStatus > 0) {
    data["askStatus"] = SlotModal.askStatus;
  }
  if (SlotModal.data["special"]){
    data["special"] = SlotModal.data["special"];
    addFilteredUsersToData(data);
  }

  const selectedColor = getSelectedColorBox();
  if (selectedColor) {
    data["color"] = selectedColor;
  }

  // for Edit
  if (!extract && SlotModal.slot) {
    return data
  }

  // for Create
  data["comment"] = $("#slot-modal-comment").val();
  return data;
}

// addFilteredUsersToData adds a string of visible user ids to post data if we have filters
// Note: this is copied from slot_crud file
export function addFilteredUsersToData(data) {
  const filterModal = window[MODAL_ID.TAG_ROLE_FILTER];
  if (data["special"] != "all" || !filterModal?.hasAnyActiveFilters) {
    return;
  }

  const visibleUsers = [];
  // For now, go to the DOM to get visible users and their ids. In the future, this will be a prop or data value.
  $('.user-row').each(function(_, element) {
    visibleUsers.push($(element).data("id"));
  })

  data["users"] = visibleUsers.join(",");

}

// Construct data object from selected slot template
function constructDataFromSlotTemplate(dateUnix, userID, special) {
  if (!selectedTemplate) {
    return;
  }
  const slot = SlotMap[selectedTemplate.id];

  if (dashboard.alertStaffIfVacationMaxedOut(slot)) {
    alertModal.alert("月休み希望数を超えています。", false);
    return;
  }

  const date = dateFromSecondsTimestamp(dateUnix);
  const dateString = goDateString(date); // YYYY-MM-DD date string is used for data purposes.

  let from = "", to = "", type, patternID, purpose, rest, locked, color, roleID, memo;

  if (selectedTemplate.type === "pattern") {
    type = 0;
    patternID = selectedTemplate.id;
  } else if (selectedTemplate.type === "slot" && slot) {
    if (!slot.HideFrom) {
      from = slot.FromLabel;
    }
    if (!slot.HideTo) {
      to = slot.ToLabel;
    }
    type = slot.Type;
    purpose = slot.Purpose;
    rest = slot.Rest;
    locked = slot.Locked;
    color = slot.Color;
    roleID = slot.RoleID;
    memo = slot.Memo;
  } else if (selectedTemplate.type === "role") {
    roleID = selectedTemplate.id;
    type = selectedTemplate.slotType;
  } else {
    return;
  }

  const data = {
    "from": from,
    "to": to,
    "comID": activeCom.ID,
    "date": dateString,
    "type": type,
    "purpose": purpose,
    "rest": rest,
    "locked": locked,
    "edited": activeUser.canEdit,
    "userID": userID,
    "color": color,
    "patternID": patternID,
    "roleID": roleID,
    "special": special,
    "isMadeFromTemplate": true,
    "memo": memo,
  };

  addFilteredUsersToData(data);

  return data;
}

export async function putSlotAfterDrag(slotID, data, oldSlot, event) {
  try {
    const response = await slotPutRequest(slotID, data);
    if (isUsingRequestSlots) {
      handleDragRequestSlotDOM(response, event, oldSlot);
    }
    const slot = getFirstSlotFromResponse(response);
    addToUndoStack(slot, structuredClone(oldSlot), false, true);
    handleDragSlotSuccess(response);
  }
  catch (err) {
    alertModal.alert(errors.SLOT.UPDATE);
    return;
  }
}

export function handleDragSlotSuccess(response) {
  let slot;
  if (!response.success) {
    alertModal.alert(errors.SLOT.UPDATE, true);
    return
  }
  // update labels if type is 0
  if (!response.addList || response.addList.length == 0) {
    slot = response.slot
    response.addList = [ response.slot ];
  }

  if (response.addList && response.addList.length > 0) {
    for (let i = 0; i < response.addList.length; i++) {
      slot = response.addList[i];
      SlotModal.onDelete(slot.ID, false);
    }

    for (let i = 0; i < response.addList.length; i++) {
      slot = response.addList[i];
      SlotMap[slot.ID] = slot;
      SlotModal.onSubmit(slot, true);
    }

    // Stats requires special handling for linked slots.
    const isLinkedSlotsDrag = response.addList[0].LinkNext || response.addList[0].LinkPrev;
    if (isLinkedSlotsDrag) {
      handleStatsLinked(response.addList);
    } else {
      response.addList.forEach(s => {
        handleRawSlotChange(s);
      })
    }
  }

  if (response.deleteList) {
    for (let i = 0; i < response.deleteList.length; i++) {
      SlotModal.onDelete(response.deleteList[i]);
    }
  }
}

// DOM manipulation when Requested Slot settings is ON
function handleDragRequestSlotDOM(response, event, oldSlot) {
  // data response changes when Request slot setting is ON,
  // When we drag a slot created by staff, we receive 2 slots data, (response.slot and response.addList)
  // this is to make all these slots are on the same array (addList) in order to render them
  if (response.slot && response.addList.length == 1) {
    response.addList.push(response.slot);
  }
  // Send the dragged slot to its original cell when its a slot created by Staff
  $(`#date-${oldSlot.UserID}_${oldSlot.Date} .vue-cell-dropzone`).append(event.originalSource);
  $(event.originalSource).removeClass("disabled")
}

// Submit function for creating/editing slot
let isSubmitting = false;
async function summarySlotSubmit(extract) {
  try {
    if (isSubmitting) {
      return;
    }
    if (!extract && $("#slot-submit").is(":hidden")) {
      return;
    }
    $(SLOT_SUBMIT_BUTTON_ID.SUBMIT).addClass("loading");
    isSubmitting = true;

    const data = constructDataFromSlotModal(extract);
    if (!data) {
      return;
    }
    let response;
    if (!extract && SlotModal?.slot) {
      response = await slotPutRequest(SlotModal.slot.ID, data);
      const slot = getFirstSlotFromResponse(response);
      addToUndoStack(slot, structuredClone(SlotMap[slot.ID]));
      handleUpdateSuccess(response, true);
    } else {
      response = await slotPostRequest(data);
      const slot = getFirstSlotFromResponse(response);
      addToUndoStack(slot);
      handleCreateSuccess(response, data);
    }
  }
  catch (err) {
    alertModal.alert(errors.SLOT.CREATE, true);
  } finally {
    isSubmitting = false;
    $(SLOT_SUBMIT_BUTTON_ID.SUBMIT).removeClass("loading");
  }

  SlotModal.close();
}

function handleSlotSubmit() {
  return summarySlotSubmit(false);
}

function handleSlotExtract() {
  // Bandaid fix when submitting repeated slot, until we find better solution,
  // We ask users if they want to edit and remove this slot from the repeated slots and create a new one instead.
  if (!confirm("このリピートシフトは解除され新規のシフトに変更されますが、よろしいですか？")) {
    return;
  }
  return summarySlotSubmit(true);
}

function handleSlotCommentExtract() {
  if (!confirm("コメント追加により、このリピートシフトは解除され新規のシフトに変更されますが、よろしいですか？")) {
    return;
  }
  return summarySlotSubmit(true);
}

// Submit function for deleting slot
function summarySlotDelete() {
  if (!SlotModal.slotID) {
    return;
  }
  if (SlotModal.slot.Repeat) {
    showDashModal(true, DELETE_SLOT_PARAM_STR.DELETE_REPEAT);
    return;
  }
  handleSummarySlotDelete();
  return;
}

async function handleSummarySlotDelete() {
  $(SLOT_SUBMIT_BUTTON_ID.DELETE).addClass("loading");
  try {
    const response = await slotDeleteRequest(SlotModal.slotID);
    // Since we currently use Modal to delete slots, we grab the ID from the SlotModal object
    // We currently do not support undo when doing bulk delete, because we also don't have bulk create for slots.
    addToUndoStack(structuredClone(SlotMap[SlotModal.slotID]), null, true);
    handleDeleteSuccess(response);
  }
  catch (err) {
    return alertModal.alert(errors.SLOT.DELETE);
  }
  finally {
    SlotModal.close();
  }
}

async function addSummarySlotTemplate(dateUnix, userID, special) {
  const data = constructDataFromSlotTemplate(dateUnix, userID, special);

  try {
    const response = await slotPostRequest(data);
    const slot = getFirstSlotFromResponse(response);
    addToUndoStack(slot);
    return handleTemplateCreateSuccess(response);
  }
  catch (err) {
    return alertModal.alert(errors.SLOT.CREATE);
  }
}

function undoActionCallback(undoActionType, response, isDrag) {
  switch (undoActionType) {
    case ACTIONS_TYPE.CREATE:
      handleCreateSuccess(response);
      break;
    case ACTIONS_TYPE.DELETE:
      handleDeleteSuccess(response);
      break;
    case ACTIONS_TYPE.UPDATE:
      if (isDrag) {
        handleDragSlotSuccess(response);
      } else {
        handleUpdateSuccess(response, true);
      }
      break;
  }
}

export async function undoHandler() {
  const lastAction = getLastUndoStack();

  if (!lastAction) {
    return;
  }

  enableUndoLoading();
  const { undoActionType, slot, newSlot, isDrag } = lastAction;

  try {
    const response = await undoActionRequest(undoActionType, slot, newSlot);
    undoActionCallback(undoActionType, response, isDrag);
    removeLastActionFromUndoStack();
    removeDeletedSlotInStack(undoActionType, slot.ID);
  } catch (err) {
    alertModal.alert("unable to undo:", err);
  } finally {
    disableUndoLoading();
  }
}

// Override handleAddButtonClick function from dashboard_base file
export function handleSummaryAddButtonClick(dateUnix, userID, slotID, formValues = {}) {

  if (!selectedTemplate) {
    openSlotModal(dateUnix, userID, slotID, formValues);
    return;
  }

  addSummarySlotTemplate(dateUnix, userID, formValues.special);
  return;
}

async function summaryDeleteRepeatSlot() {
  const choiceValue = document.querySelector('.delete-repeat-choice:checked')?.value;
  if (choiceValue) {
    const params = `?repeat=1&type=${DELETE_REPEAT_INPUT_VALUE[choiceValue]}&date=${SlotModal.slotDate}`
    $(SLOT_SUBMIT_BUTTON_ID.DELETE_REPEAT).addClass("loading");

    try {
      const response = await slotDeleteRequest(SlotModal.slotID, params)
      handleDeleteSuccess(response);
    }
    catch (err) {
      return alertModal.alert(errors.SLOT.DELETE);
    }
    finally {
      SlotModal.close();
    }
  }
}

// Helper function to get first slot (in case of split) from response, specifically or currently used for undo feature
function getFirstSlotFromResponse(response) {
  let slot = response.slot;
  if (response.addList && response.addList.length) {
    slot = response.addList[0];
  }

  return slot;
}

// For interfacing with template
export function handleRawSlotChange(rawSlot) {
  if (!rawSlot || rawSlot.LinkNext || rawSlot.LinkPrev) {
    return;
  }
  let prev = shiftStore.byId(rawSlot.ID.toString());
  if (prev && prev.isLinkedShift()) {
    let night, morning;
    if (prev.isMorningShift()) {
      morning = prev;
      night = shiftStore.byId(prev.getLinkedId());
    } else {
      night = prev;
      morning = shiftStore.byId(prev.getLinkedId());
    }
    if (morning && night) {
      prev = mergeLinkedShifts(morning, night);
    }
  }
  const options = {
    prevShift: prev,
  }
  const branch = branchStore.byId(meStore.getActiveBranchId());
  addRawSlotToShiftStore(rawSlot, branch?.timeZone, tableDates, shiftStore, branch?.id ?? 0);

  const shift = shiftStore.byId(rawSlot.ID.toString());
  // For repeat change, query and do a handleShiftChange for all
  if (rawSlot.Repeat) {
    const shifts = shiftStore.query({repeatTemplateId: rawSlot.ID});
    shifts.forEach(s => {
      summaryStats.handleShiftChange(s, options);
    })

    renderAllStatsColumns(tableDates, summaryStats, roleStore, App);
    renderAllStatsRows(summaryStats, employmentStore, roleStore, meStore.getActiveBranchId());
  }

  if (!shift) {
    return;
  }
  summaryStats.handleShiftChange(shift, options);
  renderAllStatsColumns(tableDates, summaryStats, roleStore, App);
  renderAllStatsRows(summaryStats, employmentStore, roleStore, meStore.getActiveBranchId());
  toggleUnlockSlotBtn(Object.values(SlotMap))
}

// Interface for template
export function handleStatsLinked(rawSlots) {
  if (!rawSlots && !rawSlots.length) {
    return;
  }

  let prev = shiftStore.byId(rawSlots[0].ID.toString());
  if (!prev && rawSlots[1]) {
    prev = shiftStore.byId(rawSlots[1].ID.toString())
  }
  if (prev && prev.isLinkedShift()) {
    let night, morning;
    if (prev.isMorningShift()) {
      morning = prev;
      night = shiftStore.byId(prev.getLinkedId());
    } else {
      night = prev;
      morning = shiftStore.byId(prev.getLinkedId());
    }
    if (morning && night) {
      prev = mergeLinkedShifts(morning, night);
    }
  }

  const branch = branchStore.byId(meStore.getActiveBranchId());
  // Add to store
  rawSlots.forEach(s => {
    addRawSlotToShiftStore(s, branch?.timeZone, tableDates, shiftStore, branch?.id ?? 0);
  })

  const options = {
    prevShift: prev,
  }

  // Special treatment for linked and calc stats
  rawSlots.forEach(s => {
    let shift = shiftStore.byId(s.ID.toString());
    if (!shift) {
      return;
    }
    if (shift.isLinkedShift()) {
      let night, morning;
      if (shift.isMorningShift()) {
        morning = shift;
        night = shiftStore.byId(shift.getLinkedId());
      } else {
        night = shift;
        morning = shiftStore.byId(shift.getLinkedId());
      }
      if (morning && night) {
        // Only calc stats for merged shift once, if both shift are in view.
        if (morning.getId() == shift.getId()) {
          return;
        }
        shift = mergeLinkedShifts(morning, night);
        options.countAllPeriods = true;
        options.applyRestFieldToFirstPeriodOnly = true;
      }
    }
    summaryStats.handleShiftChange(shift, options)
  })

  // Render
  renderAllStatsColumns(tableDates, summaryStats, roleStore, App);
  renderAllStatsRows(summaryStats, employmentStore, roleStore, meStore.getActiveBranchId());
}

// handle help select input change on slot modal
export function handleHelpBranchChange(branchId) {
  let selectedBranchId = branchId
  // if no branchId value, we fetch the active branch instead
  if (!branchId) {
    selectedBranchId = activeCom.ID;
  }
  getAndUpdateRolesList(selectedBranchId);
  return;
}

export function setRoleIdToSlotModal(roleId) {
  SlotModal.setRole(roleId);
  return;
}

export function handleSlotOvernightChange(rawSlot, prevRawSlot) {
  const dateStr = getDateStringsForSlot(rawSlot, 'Asia/Tokyo', null);
  const prevDateStr = getDateStringsForSlot(prevRawSlot, 'Asia/Tokyo', null);

  // No change in overnight status
  if (dateStr.length === prevDateStr.length) {
    return;
  }

  if (dateStr.length > prevDateStr.length) {
    // Slot became overnight
    SlotModal.onSubmit(rawSlot, true);
    return;
  }

  // Slot reverted to non-overnight time
  removeOvernightSlot(prevRawSlot, prevDateStr, dateStr);
}

function removeOvernightSlot(prevRawSlot, prevDateStr, dateStr) {
  const toRemoveDateStr = prevDateStr.filter(d => !dateStr.includes(d));
  const userCells = cellsMap[prevRawSlot.UserID];

  if (!userCells) {
    return;
  }

  toRemoveDateStr.forEach(dateString => {
    const cell = userCells[dateString];

    if (!cell || !cell.slots) {
      return;
    }

    delete cell.slots[prevRawSlot.ID];

    const cellApp = window[`#date-${prevRawSlot.UserID}_${dateString}`];

    if (cellApp) {
      cellApp.updateSlots(cell.slots);
    }
  });
}

// For interfacing with template
export function handleSlotDelete(rawSlot) {
  if (!rawSlot) {
    return;
  }

  let shift = shiftStore.byId(rawSlot.ID.toString());
  if (rawSlot.Repeat) {
    // For now, we get date of the repeat from the modal to delete only the "extracted" shift.
    // In the future, slot modal should pass the deleted slot entity / feId so we don't need to query.
    const oplusTimeUnix = SlotModal.data?.dateUnix;
    if (!oplusTimeUnix) {
      return;
    }
    const oplusTimeDate = dateFromSecondsTimestamp(oplusTimeUnix);
    const d = fromOplusTime(oplusTimeDate);
    const max = endDateForPeriod(d, periodTypes.DAY, "Asia/Tokyo");
    const res = shiftStore.query({repeatTemplateId: rawSlot.ID, startDateMin: d, startDateMax: max});
    shift = res[0];
  }

  if (!shift) {
    return;
  }

  let statsShift = shift;

  const options = {
    isDelete: true,
  }

  if (shift.isLinkedShift()) {
    let night, morning;
    if (shift.isMorningShift()) {
      morning = shift;
      night = shiftStore.byId(shift.getLinkedId());
    } else {
      night = shift;
      morning = shiftStore.byId(shift.getLinkedId());
    }
    if (morning && night) {
      // Only calc stats for merged shift once, if both shift are in view.
      if (morning.getId() == shift.getId()) {
        return;
      }
      statsShift = mergeLinkedShifts(morning, night);
      options.countAllPeriods = true;
    }
  }

  summaryStats.handleShiftChange(statsShift, options);
  renderAllStatsColumns(tableDates, summaryStats, roleStore, App);
  renderAllStatsRows(summaryStats, employmentStore, roleStore, meStore.getActiveBranchId());
  // delete from store
  if (statsShift.isMergedShift()) {
    shiftStore.delete(statsShift.getMorningShiftId());
    shiftStore.delete(statsShift.getNightShiftId());
    return;
  }

  shiftStore.delete(shift.getId());
  const slotList = buildSlotListFromShiftStore();
  toggleUnlockSlotBtn(slotList)
}

async function ccbizSync(startDate, endDate) {
  if (!activeCom?.hasCCBizMate) {
    alertModal.alert("The current company has no CC·BizMate id!", false)
    return;
  }

  ccbiz.sync(startDate, endDate);
}

async function initTable(props) {
  const branch = newBranchFromRaw(props.pipelineActiveCompany, props.pipelineActiveBranchSettings);
  branchStore = newBranchStore(branch);

  meStore = newMeStore(props.activeUserID, props.canEdit, branch.id);

  roleStore = newRoleStoreFromRaw(props.roles || [], props.orRolesMap);

  employmentStore = newEmploymentStoreFromRaw(props.users, branch.id);

  // Hotfix: 1 is month, 0 is week
  const defaultSetting = props?.pipelineActiveBranchSettings?.DefaultView == 0 ? viewTypes.WEEK : viewTypes.MONTH;
  const dateStr = props.urlParams.date;
  const endDateStr = props.urlParams.until ?? "";
  const listValue = props.urlParams.list;
  const viewType = calcViewType(defaultSetting, endDateStr !== "", listValue);
  // To work "properly" with oplus time, hardcode time zone to JST
  // Eventually, this will be a user setting and/or guess
  const localTimezone = "Asia/Tokyo";
  const activeBranch = branchStore.byId(meStore.activeBranchId);
  const startDate = calcTableStartDate(dateStr, localTimezone, viewType, branch.getMonthStart(), branch.getWeekStart());
  const endDate = calcTableEndDate(endDateStr, localTimezone, startDate, branch.getMonthStart(), viewType);

  tableDates = makeTableDates(startDate, endDate, periodTypes.DAY, localTimezone);

  const rolesToDisplayCount = [];
  if (props?.rootRoles) {
    props.rootRoles.forEach(role => {
      if (!role.ShowStaffCount) {
        return;
      }

      const r = roleStore.byId(role.ID);
      if (!r) {
        return;
      }

      rolesToDisplayCount.push(r)
    })
  }

  const summaryTableApp = new SummaryTableApp();
  const settings = branch.getSettings();
  summaryTableApp.provideBranchSettings(settings);
  summaryTableApp.provideCellEditMode(cellEditMode);
  summaryTableApp.provideCellsMap(cellsMap);
  summaryTableApp.provideEditModeCellsProps(computeEditModeCellProps(props.dates, props.users));
  summaryTableApp.provideDeleteMode(deleteMode);
  summaryTableApp.provideMeStore(meStore);
  summaryTableApp.provideRoleStore(roleStore);
  summaryTableApp.provideSlotLogMap(reactiveSlotLogMap);
  summaryTableApp.provideSlotMap(SlotMap);
  summaryTableApp.provideUseShiftBorders(props.useShiftBorders);

  const showMemo = settings.memoSummary && (props?.canEdit ?? false);
  summaryTableApp.initPresentationEmployeeRows(props.userViews, props.branchUserViews, props.groupUserViews, showMemo, branch.id);
  summaryTableApp.initPresentationDates(tableDates);
  const lockStatsColumns = !props?.hasProSubscription ?? true;
  const showUserAlertBadges = viewType === viewTypes.MONTH && props?.canEdit
  summaryTableApp.initPresentationStats(rolesToDisplayCount, settings, lockStatsColumns, showUserAlertBadges);

  // For now because of DOM selectors for stats and other features,
  // Must init presentation and then mount otherwise DOM selection can't find elements.
  summaryTableApp.mount();

  shiftStore = new ShiftStore();

  summaryStats = newSummaryStats(roleStore, employmentStore, shiftStore, meStore, tableDates, branch.getSettings(), localTimezone);

  for (const slotId in SlotMap){
    const rawSlot = SlotMap[slotId];

    addRawSlotToShiftStore(rawSlot, activeBranch.getTimeZone(), tableDates, shiftStore, branch.id);
  };

  for (const slotId in shiftStore.data) {
    const s  = shiftStore.byId(slotId);

    const {shift, options, doNotCalcStats} = getShiftAndOptionsForStats(s, shiftStore, branch.getSettings().splitNightShift);

    if (doNotCalcStats) {
      continue;
    }

    summaryStats.handleShiftChange(shift, options);
  }

  renderAllStatsColumns(tableDates, summaryStats, roleStore, App);
  renderAllStatsRows(summaryStats, employmentStore, roleStore, meStore.getActiveBranchId());

  if (props.canEdit) { // TODO: use meStore to determine perm level
    await initFunctionRows(props.legacyPlans, props.pipelineActiveBranchSettings.PlanEquationValue, activeBranch, tableDates);
  }
}

// initFunctionRows will initialize the plan function rows for the current view.
// Also used on dws.
export async function initFunctionRows(legacyPlans = [], planEquationValue, activeBranch, tableDates) {
  const planVarStore = new PlanVariableStore()
  const valueStore = new PlanValueStore();
  const functionStore = new PlanFunctionStore();
  const initParams = {
    legacyPlans,
    branch: activeBranch,
    planEquationValue,
  }

  initLegacyFunctions(functionStore, valueStore, planVarStore, initParams);

  await initPlanFunctions(tableDates, activeBranch, functionStore, valueStore);

  renderFunctionRows(functionStore, valueStore, tableDates, activeBranch);
}

function renderFunctionRows(functionStore, valueStore, tableDates, activeBranch) {
  const toCompute = functionStore.query({branchId: activeBranch.getId()});
  const dateToFuncIdToValue = makeComputedValuesMap(tableDates, toCompute, functionStore, valueStore);
  const tableDatesMap = {};
  tableDates.forEach(td => tableDatesMap[td.index()] = td);
  Object.keys(dateToFuncIdToValue).forEach(d => {
    const td = tableDatesMap[d];
    if (!td) {
      return;
    }

    Object.keys(dateToFuncIdToValue[d]).forEach(pfId => {
      // Hacky render for now
      const v = dateToFuncIdToValue[d][pfId];
      const oplusTime = toOplusTime(td.getDate());
      const id = `#pf-${pfId}-${timestampSecsFromDate(oplusTime)}`;
      $(id).text(roundTwoDecimals(v));
    })
  })
}

function mountCellEditMenuApp() {
  const app = createApp(CellEditMenuWrapper);
  app.mount("#cell-edit-menu-app");
}

// Scrolls to today's date in the table
export function scrollToToday() {
  var todayElm = `#date-${GetToday()}`;
  var todayElement = document.querySelector(todayElm);

  if (todayElement) {
    setTimeout(() => {
      todayElement.scrollIntoView({ behavior: "smooth", inline: "center" });
    }, 400);
  }
}

function setCalendarModal() {
  const modal = $("#calendar-modal");
  if (modal.length) {
    $("#calendar-btn-dropdown").click(() =>  modal.show());
  }
}

export function setSummaryTableColumnWidth(option) {
  // Deselect previously selected option
  const previousOption = document.querySelector('.table-config-option.selected');
  previousOption?.classList.remove('selected');

  // Select new option
  const newOption = document.getElementById(`option-${option}`);
  newOption?.classList.add('selected');

  const container = document.getElementById('summary-table-container');
  if (!container) {
    return;
  }

  container.classList.remove(ColumnWidthOptions.NARROW, ColumnWidthOptions.MEDIUM, ColumnWidthOptions.WIDE);
  container.classList.add(option);

  if (option === ColumnWidthOptions.FLEX) {
    container.classList.remove("fixed-width");
  } else {
    container.classList.add("fixed-width");
  }

  localStorage.setItem('summaryTableColumnWidth', option);
}

function initSummaryTableColumnWidth() {
  const option = localStorage.getItem('summaryTableColumnWidth');
  setSummaryTableColumnWidth(option || ColumnWidthOptions.FLEX);
}

function initUserMonthlyLaborBadges(users, branchDefaultMaxMonth) {
  if (!users || !users.length) {
    return;
  }

  // Requested vacation counts by user
  const userIds = Array.from(new Set(users.map(u => u.ID)));
  const vacationCountMap = computeRequestedVacationByUser(userIds, summaryStats.dates, summaryStats.slotStore.data);

  let laborMinutesMap = new Map();
  const employments = employmentStore.query({branchId: meStore.getActiveBranchId()});

  // Monthly labor minutes by user (from row stats)
  for (const e of employments) {
    const row = summaryStats.getRowStats(e.index()).getData();
    if (!row) {
      continue;
    }

    const userId = Number(e.getUserId());
    const laborMin = row.laborHours * 60;

    laborMinutesMap.set(userId, laborMin);
  }

  for (const u of users) {
    const row = document.getElementById(String(u.ID));
    const bu = u.BranchUser;
    const laborMinutes = laborMinutesMap.get(u.ID);
    const vacCount = vacationCountMap.get(u.ID) || 0;

    if (!row || !bu || laborMinutes === undefined) {
      continue;
    }

    const limits = {
      minMonthLaborMinutes: bu.MinMonthLaborMinutes || 0,
      maxMonthLaborMinutes: bu.MaxMonthLaborMinutes || branchDefaultMaxMonth || 0,
      monthlyVacationLimit: bu.MonthlyVacationLimit || 0,
    };
    const flags = computeMonthlyLaborFlags(limits, laborMinutes, vacCount);

    const vacationBadge = row.querySelector('.over-max-vacation');
    const overBadge = row.querySelector('.over-max-month');
    const underBadge = row.querySelector('.under-min-month');

    // Display labor badges based on flags
    if (flags.overMaxVacation) {
      vacationBadge?.classList.remove('d-none');
    }
    if (flags.overMaxMonth) {
      overBadge?.classList.remove('d-none');
    } else if (flags.underMinMonth) {
      underBadge?.classList.remove('d-none');
    }
  }
}

export function openAlertModal(errText) {
  return alertModal.alert(errText)
}

// SlotMap isn't updated when we delete a slot (to fix), shiftStore is updated but doesn't have isLocked field
function buildSlotListFromShiftStore() {
  const slotList = []
  for (const sId in SlotMap) {
    if (!shiftStore.byId(sId)) {
      continue
    }
    const slot = SlotMap[sId];
    slotList.push(slot);
  }
  return slotList;
}

export function toggleUnlockSlotBtn(slotList) {
  const btn = document.getElementById("unlock-slot-btn");
  if (!btn) {
    return;
  }

  const hasLockedSlot = slotList.some(s => {
    if (s.Repeat) {
      return Boolean(s.RepeatLocked?.length)
    }
    return s.Locked
  })

  if (hasLockedSlot) {
    btn.classList.remove("d-none");
  } else {
    btn.classList.add("d-none");
  }
}

export async function initPage(props) {
  // Legacy. Can remove when confirmed not in use.
  roleStoreOld.init(props.roles, props.orRolesMap);
  const {startDate: startOplusTime, endDate: endOplusTime} = createStartAndEndDatesFromArray(props.dates)
  activeUser = transformUser({
    canEdit: props.canEdit,
  });
  activeCom = transformCompany(props.pipelineActiveCompany);
  gBranchSettings = branchSettingsParser.parseFromRaw(props.pipelineActiveBranchSettings);

  // Table init
  buildCellsMap(cellsMap, SlotMap, endOplusTime, props.lockedOnly, gBranchSettings.splitNightShift, gBranchSettings.timezone);
  await initTable(props);

  isUsingRequestSlots = props.isUsingRequestSlots;
  readSlots(SlotMap, props.activeUserID);
  if (isUsingRequestSlots) {
    disableSaveUndo();
  }
  mountTagRoleFilterModal(props);

  const roles = roleStore.query({isOrRole: true, excludeOrRole: true})
  mountAutoModal(props, roles);

  if (props.hasAutoPerm && props.hasOpenAIPerm) {
    mountAutoAIModal(props, roles, startOplusTime, endOplusTime);
    initFooterAutoButtonClick();
  }

  mountCellEditMenuApp();
  initSlotModalRoleListComponent(activeCom.ID)
  // as of 2023-12-06 DashURL function is in dashboard_base.html line 4602
  getPageSlotURL(DashURL("slot"))
  setCalendarModal();
  initSummaryTableColumnWidth();
  toggleUnlockSlotBtn(Object.values(SlotMap))
  initFooterDropdownHandlers();

  if (props.canEdit && props.timePeriodType === "month") {
    initUserMonthlyLaborBadges(props.users, props.branchDefaultMaxMonth);
  }

  // CCbiz
  // sync button
  $(`#${ccbiz.CCBIZ_ELEMENT_IDS.SYNC_BUTTON}`).on('click', () => ccbizSync(startOplusTime, endOplusTime));

  $(`#${kot.BULK_SYNC_BUTTON}`).on('click', () => kot.bulkSync(startOplusTime, endOplusTime));
  $(`#${smarthr.SHIFT_SYNC_BUTTON}`).on('click', () => smarthr.syncShifts(props.activeUserID, meStore.getActiveBranchId(), startOplusTime, endOplusTime));

  // Override submit/delete button for modal and template functions to isolate summary page's (monthly/weekly) slot CRUD
  // In the future, it should also apply to other pages that needs the same CRUD functions, (shift_base, summary_dws)
  $(SLOT_SUBMIT_BUTTON_ID.SUBMIT).attr("onclick", "").on("click", () => handleSlotSubmit());
  $(SLOT_SUBMIT_BUTTON_ID.EXTRACT).attr("onclick", "").on("click", () => handleSlotExtract());
  $(SLOT_SUBMIT_BUTTON_ID.DELETE).attr("onclick", "").on("click", () => summarySlotDelete());
  $(SLOT_SUBMIT_BUTTON_ID.DELETE_REPEAT).attr("onclick", "").on("click", () => summaryDeleteRepeatSlot());
  $(SLOT_SUBMIT_BUTTON_ID.SUBMIT_COMMENT_EXTRACT).attr("onclick", "").on("click", () => handleSlotCommentExtract());
}


// postLocked will request the BE lock or unlock all slots for a given date range and optional set of filters, returning slots and repeat slots affected.
// Expected response:
// {
//   slots: LockSlotView[],
//   repeatSlots: LockRepeatSlotView[],
// }

// LockSlotView {
// 	id number
// 	isLocked boolean
// 	isKOTSynced boolean
// }

// LockRepeatSlotView {
// 	id           number
// 	repeatLocked string[] (oplus time timestamp strings)
// }

let isLockLoading = false;
export async function postLocked(isLocking, slotMap, cellsMap) {
  if (isLockLoading) {
    return;
  }

  const btn = document.getElementById("lock-button-wrapper");
  btn?.classList.add('loading');
  isLockLoading = true;
  // Include all search query params from summary page
  const params = new URLSearchParams(window.location.search);
  const locked = isLocking ? '1' : '0';
  params.set('locked', locked)

  const url = `${window.location.pathname}/lock_slots?${params.toString()}`;
  try {
    const rawRes = await fetch(url, {
      method: "POST",
    })

    if (rawRes.status !== 200) {
      throw new Error('failed')
    }

    const json = await rawRes.json();
    const activeBranch = branchStore.byId(meStore.activeBranchId);
    const tz = activeBranch.getTimeZone();

    for (const slot of json.slots) {
      if (!slotMap[slot.id]) {
        continue;
      }
      // Copying is required for current Vue implementation to react to field changes.
      const copy = structuredClone(slotMap[slot.id]);

      copy.Locked = slot.isLocked;
      copy.KOTSynced = slot.isKOTSynced;
      slotMap[slot.id] = copy;
      const dates = getDateStringsForSlot(copy, tz);

      for (const date of dates) {
        if (!cellsMap[copy.UserID]?.[date]) {
          continue;
        }
        updateCellAppSlots(copy.UserID, date, cellsMap[copy.UserID][date].slots)
      }

    }

    for (const repeatSlot of json.repeatSlots) {
      const prev = slotMap[repeatSlot.id];
      if (!prev) {
        continue;
      }
      // Copying is required for current Vue implementation to react to field changes.
      const copy = structuredClone(prev);

      copy.RepeatLocked = repeatSlot.repeatLocked;
      slotMap[repeatSlot.id] = copy;

      for (const lockedDate of repeatSlot.repeatLocked) {
        const dates = getDateStringsForSlot(copy, tz, new Date(lockedDate));
        for (const date of dates) {
          if (!cellsMap[copy.UserID]?.[date]) {
            continue;
          }
          updateCellAppSlots(copy.UserID, date, cellsMap[copy.UserID][date].slots)
        }
      }

      if (!prev.RepeatLocked) {
        // Sometimes RepeatLocked in SlotMap is null.
        continue;
      }

      // Cleanup previous locked dates
      for (const lockedDate of prev.RepeatLocked) {
        const dates = getDateStringsForSlot(prev, tz, new Date(lockedDate));
        for (const date of dates) {
          if (!cellsMap[copy.UserID]?.[date]) {
            continue;
          }
          updateCellAppSlots(copy.UserID, date, cellsMap[copy.UserID][date].slots)
        }
      }
    }
    const slotList = buildSlotListFromShiftStore();
    toggleUnlockSlotBtn(slotList);

  } catch {
    let msg = "確定できませんでした。";
    if (!isLocking) {
      msg = "確定解除できませんでした。"
    }
    openAlertModal(msg);
  } finally {
    isLockLoading = false;
    btn?.classList.remove('loading');
  }

}

function updateCellAppSlots(userId, date, slots) {
  if (!slots) {
    return;
  }

  const cellApp = window[`#date-${userId}_${date}`];

  if (!cellApp) {
    return;
  }

  cellApp.updateSlots(slots);
}

// Temporary solution.
// Allows SummaryTable app to mutate droppable global var in summary.html.
export function addElToDroppable(el) {
  if (!droppable) {
    return;
  }
  droppable.addContainer(el);
}

// Temporary solution.
// Allows SummaryTable app to mutate droppable global var in summary.html.
export function removeElFromDroppable(el) {
  if (!droppable) {
    return;
  }
  droppable.removeContainer(el);
}
